import {
  HelpCircle, Star, RefreshCw, Save,
  Pencil, Printer, Trash2, LogOut,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui/Spinner';
import { useSidebarFavorite } from '@/hooks/useSidebarFavorite';

// ── Button color palette ───────────────────────────────────────────────────────

const COLOR = {
  sky:     'bg-sky-500   hover:bg-sky-600   active:bg-sky-700   text-white',
  slate:   'bg-slate-600 hover:bg-slate-700 active:bg-slate-800 text-white',
  teal:    'bg-teal-500  hover:bg-teal-600  active:bg-teal-700  text-white',
  emerald: 'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white',
  amber:   'bg-amber-500 hover:bg-amber-600 active:bg-amber-700  text-white',
  orange:  'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white',
  rose:    'bg-rose-500  hover:bg-rose-600  active:bg-rose-700   text-white',
  dark:    'bg-gray-800  hover:bg-gray-900  active:bg-black       text-white',
} as const;

export type ButtonColor = keyof typeof COLOR;

export interface ToolbarButtonDef {
  key:      string;
  label:    string;
  icon:     React.ReactNode;
  color:    ButtonColor;
  onClick:  () => void;
  disabled?: boolean;
  loading?:  boolean;
}

// ── Props ──────────────────────────────────────────────────────────────────────

/**
 * ERP master-file toolbar.
 * New button is intentionally absent — records are created via code lookup.
 */
export interface MasterToolbarProps {
  onHelp?:     () => void;
  onFavorite?: () => void;
  onRefresh:   () => void;
  onSave:      () => void;
  onEdit:      () => void;
  onPrint?:    () => void;
  onDelete:    () => void;
  onExit:      () => void;

  saveEnabled?:    boolean;
  editEnabled?:    boolean;
  deleteEnabled?:  boolean;
  printEnabled?:   boolean;
  /** Refresh is always enabled (always resets to INITIAL) — default true */
  refreshEnabled?: boolean;

  saving?:  boolean;
  loading?: boolean;

  /**
   * A document's workflow steps — Submit, Approve, Reject, Post, Reverse, Cancel (ADR-004). Placed
   * after Save, in the same button style, so a document screen uses this toolbar instead of
   * drawing a second one. A master record leaves it out.
   */
  actions?: ToolbarButtonDef[];
}

// ── Component ─────────────────────────────────────────────────────────────────

export const MasterToolbar = ({
  onHelp,
  onFavorite,
  onRefresh,
  onSave,
  onEdit,
  onPrint,
  onDelete,
  onExit,
  saveEnabled    = false,
  editEnabled    = false,
  deleteEnabled  = false,
  printEnabled   = false,
  refreshEnabled = true,
  saving  = false,
  loading = false,
  actions = [],
}: MasterToolbarProps) => {
  // Default the star to the client-side favourites store (myPOS ONE parity); a page may still
  // override with its own onFavorite. Disabled only when the current route is not a nav target.
  const fav = useSidebarFavorite();
  const handleFavorite = onFavorite ?? fav.toggle;
  const favActive = !onFavorite && fav.isFavorite;

  const buttons: ToolbarButtonDef[] = [
    { key: 'help',    label: 'Help',    icon: <HelpCircle size={15} />, color: 'sky',     onClick: onHelp ?? (() => {}),  disabled: false },
    { key: 'fav',     label: favActive ? 'Favourited' : 'Favorite', icon: <Star size={15} className={cn(favActive && 'fill-current')} />, color: favActive ? 'amber' : 'slate', onClick: handleFavorite, disabled: !onFavorite && !fav.navItem },
    { key: 'refresh', label: 'Refresh', icon: <RefreshCw size={15} />,  color: 'teal',    onClick: onRefresh,               disabled: !refreshEnabled || loading },
    { key: 'save',    label: 'Save',    icon: <Save size={15} />,       color: 'emerald', onClick: onSave,                  disabled: !saveEnabled || loading, loading: saving },
    ...actions.map(a => ({ ...a, disabled: a.disabled || saving || loading })),
    { key: 'edit',    label: 'Edit',    icon: <Pencil size={15} />,     color: 'amber',   onClick: onEdit,                  disabled: !editEnabled  || saving || loading },
    { key: 'print',   label: 'Print',   icon: <Printer size={15} />,    color: 'orange',  onClick: onPrint ?? (() => {}),   disabled: !printEnabled || saving || loading },
    { key: 'delete',  label: 'Delete',  icon: <Trash2 size={15} />,     color: 'rose',    onClick: onDelete,                disabled: !deleteEnabled || saving || loading },
    { key: 'exit',    label: 'Exit',    icon: <LogOut size={15} />,     color: 'dark',    onClick: onExit,                  disabled: saving },
  ];

  return (
    <div data-testid="master-toolbar" className="flex items-center gap-1.5 flex-wrap px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
      {buttons.map(btn => (
        <button
          key={btn.key}
          type="button"
          onClick={btn.onClick}
          disabled={btn.disabled || btn.loading}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 h-8 rounded text-xs font-semibold',
            'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-white/50',
            'disabled:opacity-40 disabled:cursor-not-allowed',
            COLOR[btn.color],
          )}
          title={btn.label}
        >
          {btn.loading ? <Spinner size="sm" className="text-white" /> : btn.icon}
          <span className="hidden sm:inline">{btn.label}</span>
        </button>
      ))}
    </div>
  );
};
